import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import { hexToRGB } from "@shared/utils/misc";
import { typography } from "@shared/common/Themes/typography";
import {
  BillingAgreementDetails,
  LanguageNameSpaces,
  PaymentType,
} from "@shared/types";
import GroupImage from "common/Images/GroupImage";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface PaymentInfoProps {
  billingAgreementDetails: BillingAgreementDetails;
}

const PaymentInfo = ({ billingAgreementDetails }: PaymentInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const durationToDisplay: Record<number, string> = {
    30: "month",
    360: "year",
  };

  const isSlots =
    billingAgreementDetails.payment_type === PaymentType.SLOTS ||
    billingAgreementDetails.payment_type === PaymentType.PRORATED_SLOTS;

  const price = billingAgreementDetails.upgrade_price;
  const duration =
    billingAgreementDetails.subscription_days in durationToDisplay
      ? `1 ${durationToDisplay[billingAgreementDetails.subscription_days]}`
      : `${billingAgreementDetails.subscription_days} days`;
  const planType = isSlots
    ? t("api_slots.num_api_slots.title")
    : billingAgreementDetails.sub_group_name ??
      `${billingAgreementDetails.subscription_type || ""} Plan`;

  let subscriptionTypeAndDuration = (
    <SubscriptionTypeAndDurationWrapper>
      <Text $weight={typography.fontWeights.MEDIUM} $ellipsis={true}>
        {planType}
      </Text>
      {isSlots && billingAgreementDetails.number_of_clients != null && (
        <Text type="secondary">
          x{billingAgreementDetails.number_of_clients} slots
        </Text>
      )}
      {!isSlots && <Text type="secondary">{duration}</Text>}
    </SubscriptionTypeAndDurationWrapper>
  );

  if (billingAgreementDetails.sub_group_image) {
    subscriptionTypeAndDuration = (
      <Flex style={{ overflow: "hidden" }}>
        <GroupImage
          src={billingAgreementDetails.sub_group_image}
          style={{ marginRight: 12 }}
        />
        {subscriptionTypeAndDuration}
      </Flex>
    );
  }

  const planPrice = (
    <Text
      $weight={
        theme.isNewLook
          ? typography.fontWeights.MEDIUM
          : typography.fontWeights.SEMI_BOLD
      }
    >
      ${theme.isNewLook ? formatMoney(price) : price} USD
    </Text>
  );

  return (
    <PaymentInfoWrapper>
      {subscriptionTypeAndDuration}
      {planPrice}
    </PaymentInfoWrapper>
  );
};

export default PaymentInfo;

const PaymentInfoWrapper = styled.div`
  width: 100%;
  background-color: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.1)};
  border-radius: 4px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0 8px 0;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 16px;
      margin: 20px 0 0;
      padding: 16px;
      border-radius: 12px;
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      background-color: ${p.theme.colors.cardLightBgColor};
      text-align: left;
      font-variant-numeric: tabular-nums;
    `}
`;
const SubscriptionTypeAndDurationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
`;
